import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import {
  type AppPermission,
  type AppUser,
  type CurrentUser,
  api,
  formatDate,
} from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import {
  MoreHorizontal,
  Plus,
  Search,
  ShieldCheck,
  ShieldOff,
  UserCog,
  UserPlus,
  Users,
} from "lucide-react"
import { AppUserFormSheet } from "@/components/AppUserFormSheet"

type Props = {
  currentUser: CurrentUser
  onUsersRefresh: (users: AppUser[]) => void
}

export function AppUsersPage({ currentUser, onUsersRefresh }: Props) {
  const [users, setUsers] = useState<AppUser[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<AppUser | null>(null)
  const [toggleTarget, setToggleTarget] = useState<AppUser | null>(null)
  const [toggling, setToggling] = useState(false)

  const isAdmin = currentUser.permissions.includes("admin")

  useEffect(() => {
    if (!isAdmin) return
    api
      .getAppUsers()
      .then((r) => setUsers(r.users))
      .catch(() => toast.error("Erro ao carregar usuários."))
      .finally(() => setLoading(false))
  }, [isAdmin])

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    if (!q) return users
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        u.fullName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    )
  }, [users, search])

  const handleSaved = (updated: AppUser[]) => {
    setUsers(updated)
    onUsersRefresh(updated)
  }

  const handleToggle = async () => {
    if (!toggleTarget) return
    setToggling(true)
    try {
      const res = await api.setAppUserStatus(
        toggleTarget.username,
        !toggleTarget.isActive
      )
      setUsers(res.users)
      onUsersRefresh(res.users)
      toast.success(res.log)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao alterar status."
      )
    } finally {
      setToggling(false)
      setToggleTarget(null)
    }
  }

  if (!isAdmin) {
    return (
      <div className="flex w-full flex-1 flex-col items-start justify-start p-4 sm:p-6 md:p-8">
        <p className="text-sm text-muted-foreground">
          Sem permissão para acessar esta seção.
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-hidden p-4 sm:p-6 md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">
            Usuários do App
          </h1>
          <p className="text-sm text-muted-foreground">
            Gerencie quem acessa este painel e suas permissões.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditingUser(null)
            setSheetOpen(true)
          }}
        >
          <UserPlus />
          Novo usuário
        </Button>
      </div>

      <Card className="min-h-0 flex-1 overflow-hidden">
        <CardHeader className="shrink-0 pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-sm font-medium">
                Usuários cadastrados
              </CardTitle>
              <CardDescription className="text-xs">
                {loading
                  ? "Carregando..."
                  : `${users.length} usuário(s) · ${users.filter((u) => u.isActive).length} ativo(s)`}
              </CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar usuário..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          {loading ? (
            <div className="w-full space-y-2 px-6 pb-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex min-h-48 w-full flex-col items-start gap-3 px-6 py-12 sm:min-h-56">
              <Users className="size-8 text-muted-foreground/40" />
              <p className="w-full text-left text-sm text-muted-foreground">
                {search
                  ? "Nenhum usuário encontrado."
                  : "Nenhum usuário cadastrado."}
              </p>
              {!search && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-1"
                  onClick={() => {
                    setEditingUser(null)
                    setSheetOpen(true)
                  }}
                >
                  <Plus />
                  Adicionar usuário
                </Button>
              )}
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full min-w-[820px] border-separate border-spacing-0 text-sm">
                <thead>
                  <tr>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Usuário
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Nome / E-mail
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Status
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Permissões
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Criação
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr
                      key={u.username}
                      className="transition-colors last:[&>td]:border-0 hover:bg-muted/30"
                    >
                      <td className="border-b px-4 py-3">
                        <span className="font-mono text-xs text-muted-foreground">
                          {u.username}
                        </span>
                      </td>
                      <td className="border-b px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {u.fullName || "-"}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {u.email || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="border-b px-4 py-3">
                        <Badge
                          variant={u.isActive ? "default" : "secondary"}
                          className="font-mono text-[10px]"
                        >
                          {u.isActive ? "ativo" : "inativo"}
                        </Badge>
                      </td>
                      <td className="border-b px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {u.permissions.map((p) => (
                            <PermBadge key={p} p={p} />
                          ))}
                        </div>
                      </td>
                      <td className="border-b px-4 py-3 text-xs text-muted-foreground">
                        {formatDate(u.createdAt)}
                      </td>
                      <td className="border-b px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="size-7 p-0"
                            >
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingUser(u)
                                setSheetOpen(true)
                              }}
                            >
                              <UserCog className="mr-2 size-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setToggleTarget(u)}
                              className={
                                u.isActive
                                  ? "text-destructive focus:text-destructive"
                                  : ""
                              }
                            >
                              {u.isActive ? (
                                <>
                                  <ShieldOff className="mr-2 size-4" />
                                  Desativar
                                </>
                              ) : (
                                <>
                                  <ShieldCheck className="mr-2 size-4" />
                                  Ativar
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AppUserFormSheet
        open={sheetOpen}
        editingUser={editingUser}
        currentUser={currentUser}
        onClose={() => setSheetOpen(false)}
        onSaved={handleSaved}
      />

      <AlertDialog
        open={toggleTarget !== null}
        onOpenChange={(o) => !o && setToggleTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {toggleTarget?.isActive ? "Desativar" : "Ativar"} usuário
            </AlertDialogTitle>
            <AlertDialogDescription>
              {toggleTarget?.isActive
                ? `O usuário "${toggleTarget?.username}" perderá acesso ao painel imediatamente.`
                : `O usuário "${toggleTarget?.username}" voltará a ter acesso ao painel.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggling}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggle}
              disabled={toggling}
              className={
                toggleTarget?.isActive
                  ? "text-destructive-foreground bg-destructive hover:bg-destructive/90"
                  : ""
              }
            >
              {toggling
                ? "Aguarde..."
                : toggleTarget?.isActive
                  ? "Desativar"
                  : "Ativar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

const PERM_STYLE: Record<AppPermission, string> = {
  admin: "bg-violet-500/15 text-violet-400 border-violet-500/20",
  create: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  download: "bg-sky-500/15 text-sky-400 border-sky-500/20",
  view: "bg-muted text-muted-foreground border-border",
}

function PermBadge({ p }: { p: AppPermission }) {
  return (
    <span
      className={`inline-flex rounded border px-1.5 py-0.5 font-mono text-[10px] ${PERM_STYLE[p]}`}
    >
      {p}
    </span>
  )
}
